import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  Validators,
} from '@angular/forms';
import { SmartForm } from '@certemy/ui-components';
import { Subscription } from 'rxjs';
import { PdfTemplateField, PdfTemplateRadioOption } from './pdf-template-field.interface';
import { v4 as uuidv4 } from 'uuid';

const FIELD_TYPE = [
  { name: 'Generic', id: 1 },
  { name: 'Boolean', id: 2 },
  { name: 'Radio', id: 3 },
  { name: 'Phone', id: 4 },
  { name: 'Dynamic Form Signature', id: 5 },
  { name: 'Log Step Signature', id: 6 },
  { name: 'Date', id: 7 },
];

@Component({
  selector: '[pdf-template-field]',
  templateUrl: './pdf-template-field.component.html',
  styleUrls: ['./pdf-template-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PdfTemplateFieldComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: PdfTemplateFieldComponent,
    },
  ],
})
export class PdfTemplateFieldComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Output() public deleteField = new EventEmitter<string>();

  form: UntypedFormGroup;
  radioOptions: UntypedFormGroup;

  selectedType: number = null;

  value: PdfTemplateField = null;
  radioOptionsArray: PdfTemplateRadioOption[] = [];

  constructor() {}

  ngOnInit() {
    this.form = new UntypedFormGroup({
      fieldType: new UntypedFormControl(null, Validators.compose([Validators.required])),
      name: new UntypedFormControl(
        null,
        Validators.compose([Validators.required, Validators.pattern('[a-zA-Z0-9()/\\\\_ -]+$')]),
      ),
      jsonName: new UntypedFormControl(null, Validators.compose([Validators.required, Validators.pattern('[a-zA-Z0-9-_]+$')])),
    });

    this.form.get('fieldType').valueChanges.subscribe((val: number) => {
      this.selectedType = val;
    });

    this.radioOptions = new UntypedFormGroup({});

    this.form.addControl('radioOptions', this.radioOptions);
  }

  ngOnDestroy() {
    for (let sub of this.onChangeSubs) {
      sub.unsubscribe();
    }
  }

  onTouched: Function = () => {};

  onChangeSubs: Subscription[] = [];

  get options(): SmartForm.FieldOption[] {
    return FIELD_TYPE;
  }

  writeValue(field: PdfTemplateField) {
    this.value = field;

    if (field) {
      this.form.patchValue({
        fieldType: field.fieldType,
        name: field.name,
        jsonName: field.jsonName,
      });

      if (field.radioOptions) {
        field.radioOptions.map(item => {
          this.addRadioControl(item);
        });

        this.radioOptionsArray = field.radioOptions;
      }
    }
  }

  registerOnChange(onChange: any) {
    const sub = this.form.valueChanges.subscribe(onChange);
    this.onChangeSubs.push(sub);
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  validate(control: AbstractControl) {
    if (this.form.valid) {
      return null;
    }

    let errors: any = {};

    errors = this.addControlErrors(errors, 'fieldType');
    errors = this.addControlErrors(errors, 'name');
    errors = this.addControlErrors(errors, 'jsonName');
    errors = this.addRadioErrors(errors);

    return errors;
  }

  addControlErrors(allErrors: any, controlName: string): any {
    const errors = { ...allErrors };

    const controlErrors = this.form.controls[controlName].errors;

    if (controlErrors) {
      errors[controlName] = controlErrors;
    }

    return errors;
  }

  addRadioErrors(allErrors: any): any {
    const errors = { ...allErrors };

    for (const item of this.radioOptionsArray) {
      if (this.radioOptions.controls[item.pdfTemplateFieldRadioOptionId]) {
        const displayNameErrors: any = (this.radioOptions.controls[item.pdfTemplateFieldRadioOptionId] as UntypedFormGroup)
          .controls['displayName'].errors;
        const optionValueErrors: any = (this.radioOptions.controls[item.pdfTemplateFieldRadioOptionId] as UntypedFormGroup)
          .controls['optionValue'].errors;

        if (displayNameErrors) {
          errors[`${item.pdfTemplateFieldRadioOptionId}_displayName`] = displayNameErrors;
        }

        if (optionValueErrors) {
          errors[`${item.pdfTemplateFieldRadioOptionId}_optionValue`] = optionValueErrors;
        }
      }
    }

    return errors;
  }

  _deleteField() {
    this.deleteField.emit(this.value.pdfTemplateFieldId);
  }

  addItem() {
    const newItem: PdfTemplateRadioOption = {
      pdfTemplateFieldRadioOptionId: uuidv4(),
      displayName: null,
      optionValue: null,
    };

    this.value.radioOptions.push(newItem);

    this.addRadioControl(newItem);
  }

  deleteItem(i: number) {
    const deletedItem: PdfTemplateRadioOption[] = this.value.radioOptions.splice(i, 1);

    this.radioOptions.removeControl(deletedItem[0].pdfTemplateFieldRadioOptionId);
  }

  addRadioControl(control: PdfTemplateRadioOption) {
    this.radioOptions.addControl(
      control.pdfTemplateFieldRadioOptionId,
      new UntypedFormGroup({
        displayName: new UntypedFormControl(
          control.displayName,
          Validators.compose([Validators.required, Validators.pattern('[a-zA-Z0-9()/\\\\-_ ]+$')]),
        ),
        optionValue: new UntypedFormControl(
          control.optionValue,
          Validators.compose([Validators.required, Validators.pattern('[a-zA-Z0-9-_]+$')]),
        ),
      }),
    );
  }
}
